import { CheckCircle } from '@untitledui/icons';
import React, { useCallback, useMemo } from 'react';

import { EmptyState } from '@/lib/base/empty-state/empty-state';
import { getActionsLockReason } from '@/lib/proposals/utils/clarifyingGating';

import type {
  ProposalDetail,
  ProposedAction,
} from '../../lib/proposals/core/types';
import { useNoOpDismissOptional } from '../../lib/proposals/hooks/NoOpDismissContext';
import {
  useProposalSelection,
  useProposalSelectionOptional,
} from '../../lib/proposals/hooks/ProposalSelectionContext';
import { useProposalContentDerivedState } from '../../lib/proposals/hooks/useProposalContentDerivedState';
import { getTriggerActionIds } from '../../lib/proposals/hooks/useTriggerPreview';
import { DeliveryStatesProvider } from '../../lib/proposals/linkedin/DeliveryStatesContext';
import type { SelectionState } from '../../lib/proposals/types/selection';
import ActionOverview from './ActionOverview';
import { ActionsLockedNotice } from './ActionsLockedNotice';
import { MultiActionTimeline } from './MultiActionTimeline';
import ProposalUIRenderer from './ProposalUIRenderer';
import TriggerPreview from './TriggerPreview';

/**
 * Thin wrapper that consumes ProposalSelectionContext.
 * Re-renders when selection changes, but callers do NOT.
 * Defined outside the component so React treats it as a stable component.
 */
const SelectionAwareEntity = React.memo(function SelectionAwareEntity(
  props: Omit<
    React.ComponentProps<typeof MultiActionTimeline>,
    'selectionState'
  >
) {
  const { selectionState } = useProposalSelection();
  return <MultiActionTimeline {...props} selectionState={selectionState} />;
});

/**
 * Variant that uses an externally-provided selectionState instead of reading
 * from ProposalSelectionProvider. Used when the provider is not available.
 */
const ExternalSelectionEntity = React.memo(function ExternalSelectionEntity(
  props: React.ComponentProps<typeof MultiActionTimeline>
) {
  return <MultiActionTimeline {...props} />;
});

export interface ProposalContentProps {
  proposal: ProposalDetail;
  onUpdate: (action: ProposalDetail) => void;
  onActionUpdate: (
    updater: (action: ProposedAction) => ProposedAction | null
  ) => void;
  isProcessing: boolean;
  onOpenThread?: () => void;
  optimisticMessage?: string | null;
  /** Optional external selection state (for contexts without ProposalSelectionProvider) */
  selectionState?: SelectionState;
  /**
   * When true, renders the proposal in read-only mode: no edit buttons,
   * no checkboxes, no update callbacks passed to child components.
   * Used for resolved proposals (approved, rejected, failed, withdrawn).
   */
  readOnly?: boolean;
  /**
   * True only while the refine turn kicked off by submitting the gating
   * clarifying answers is running — NOT for ordinary chat turns. Gates the
   * `refining` lock state; a generic "processing" boolean here would re-lock
   * the cards on every later chat turn (see getActionsLockReason).
   */
  clarifyingRefineInFlight?: boolean;
  /**
   * Clarifying-questions card, rendered directly under the Action Overview /
   * trigger preview so the agent's request for input reads before the action
   * cards. A slot (not threaded state) so this component stays ignorant of
   * the clarifying-answers machinery — the host owns it. The footer renders
   * only the chat input and the approve/reject buttons (task 4).
   */
  clarifyingPanel?: React.ReactNode;
}

/**
 * Core proposal rendering component shared between sidebar and inline chat contexts.
 * Renders three content branches:
 *   1. Custom UI proposals → ProposalUIRenderer
 *   2. Table proposals → flex layout with SelectionAwareEntity
 *   3. Standard proposals → ActionOverview (reasoning + triggers) or, when there
 *      is no reasoning, a standalone TriggerPreview, + SelectionAwareEntity
 *
 * No sidebar-specific dependencies (no header, no footer, no animations).
 * Must be rendered inside ProposalSelectionProvider for SelectionAwareEntity to work.
 */
export function ProposalContent({
  proposal,
  onUpdate,
  onActionUpdate,
  isProcessing,
  onOpenThread,
  optimisticMessage,
  selectionState: externalSelectionState,
  readOnly = false,
  clarifyingRefineInFlight = false,
  clarifyingPanel,
}: ProposalContentProps) {
  // Stable callback for TriggerPreview — wraps the optional onOpenThread prop.
  // Returns undefined when onOpenThread is not provided so downstream components
  // (e.g. TriggerPreview) can use truthiness to hide the "View Thread" button.
  const handleOpenThread = useCallback(() => {
    onOpenThread?.();
  }, [onOpenThread]);
  const openThreadProp = onOpenThread ? handleOpenThread : undefined;

  // Optional bridge to the sibling StickyFooter's no-op dismiss handler.
  // `null` outside a NoOpDismissProvider, or when no handler is registered.
  const noOpDismiss = useNoOpDismissOptional();

  // Fully no-op update actions (proposed value already matches the current
  // value) are never rendered — approving them would be a no-op write. The
  // context computes this live, against the CRM store, rather than the
  // proposal-time snapshot. Threaded into every action-rendering surface
  // below.
  // Optional: ProposalContent also renders provider-less (read-only /
  // external-selection consumers, e.g. resolved-proposal previews). Without a
  // provider there is no live no-op filtering, so every action is visible.
  const selection = useProposalSelectionOptional();
  const visibleActions = selection?.visibleActions ?? proposal.actions;
  const visibleProposal = useMemo(
    () =>
      visibleActions.length === proposal.actions.length
        ? proposal
        : { ...proposal, actions: visibleActions },
    [proposal, visibleActions]
  );

  // The trigger (source email/CRM event) is a property of the ORIGINAL
  // proposal, keyed off its primary action id. Derive the trigger action ids
  // from the unfiltered proposal — never from `visibleProposal` — so hiding a
  // no-op action can't shift `actions[0]` and make ActionOverview/TriggerPreview
  // fetch the wrong action (or 404 and blank the trigger). See
  // getTriggerActionIds.
  const triggerActionIds = useMemo(
    () => getTriggerActionIds(proposal),
    [proposal]
  );

  // The Action Overview consolidates reasoning + triggers into one card. Only
  // show it when there's reasoning to render (the AI summary, or the proposal
  // description as a fallback). When there's no reasoning, fall back to the
  // standalone trigger preview ("triggers as previous") so trigger-only
  // proposals (e.g. onboarding actions with no reasoning) don't get an empty
  // overview header.
  const hasReasoning = !!(proposal.reasoning_summary || proposal.description);

  // Check if this proposal uses custom UI definition (no actions, has ui_definition)
  const hasCustomUI = proposal.ui_definition && proposal.actions.length === 0;

  // True when every action on the proposal is a no-op update — nothing
  // visible remains to render. Custom-UI proposals never qualify (they have
  // no property-update actions to begin with).
  const allActionsNoOp =
    !hasCustomUI && proposal.actions.length > 0 && visibleActions.length === 0;

  const {
    isTableProposal,
    hasTrigger,
    deliveryActionIds,
    expectsDeliveryRows,
  } = useProposalContentDerivedState(proposal, hasCustomUI, visibleProposal);

  // Actions are locked while a gating clarifying question is unanswered, or
  // while the submitted answers are being applied. Scoped to the panel's
  // presence so the inline render path (which passes no `clarifyingPanel`)
  // is unaffected.
  const lockReason = useMemo(
    () =>
      clarifyingPanel != null
        ? getActionsLockReason(
            proposal.clarifying_questions,
            clarifyingRefineInFlight
          )
        : null,
    [clarifyingPanel, proposal.clarifying_questions, clarifyingRefineInFlight]
  );
  const actionsLocked = lockReason !== null;

  // In readOnly mode, suppress selection state (hides checkboxes) and treat
  // the proposal as processing (which puts ActionRenderer into 'readonly' mode,
  // hiding edit buttons). Update callbacks become no-ops. Locked actions
  // (unanswered/refining gating question) are treated the same way — otherwise
  // the cards go read-only but the checkboxes stay live, letting the user
  // change which actions would be approved while the UI says they're locked.
  const suppressSelection = readOnly || actionsLocked;
  const effectiveIsProcessing = isProcessing || suppressSelection;
  const effectiveSelectionState = suppressSelection
    ? undefined
    : externalSelectionState;

  // Choose entity component: use external selection state when provided (no provider needed),
  // otherwise use the context-aware wrapper that reads from ProposalSelectionProvider.
  // When selection is suppressed (readOnly or locked), always use
  // ExternalSelectionEntity with no selectionState so the checkboxes are gone,
  // not merely non-functional.
  const EntityComponent =
    suppressSelection || externalSelectionState !== undefined
      ? ExternalSelectionEntity
      : SelectionAwareEntity;
  const entitySelectionProps = useMemo(
    () =>
      effectiveSelectionState
        ? { selectionState: effectiveSelectionState }
        : {},
    [effectiveSelectionState]
  );

  if (hasCustomUI && proposal.ui_definition) {
    return (
      <div className='flex-1 overflow-y-auto overflow-x-hidden relative z-[2]'>
        <div className='relative p-3 sm:p-4 lg:p-6 max-w-[1000px] mx-auto w-full'>
          <ProposalUIRenderer uiDefinition={proposal.ui_definition} />
        </div>
      </div>
    );
  }

  if (isTableProposal) {
    return (
      /* Table proposals — flex layout lets tables fill available space with internal scrolling */
      <DeliveryStatesProvider
        orgId={proposal.org}
        actionIds={deliveryActionIds}
        expectsDeliveryRows={expectsDeliveryRows}
      >
        <div className='flex-1 flex flex-col min-h-0 overflow-y-auto relative z-[2] p-3 sm:p-4 lg:p-6'>
          {/* Action Overview when there's reasoning to show; otherwise the
              standalone trigger preview ("triggers as previous"). */}
          <div className='flex-shrink-0'>
            {hasReasoning ? (
              <ActionOverview
                proposal={proposal}
                hasTrigger={hasTrigger}
                onOpenThread={openThreadProp}
              />
            ) : (
              <TriggerPreview
                proposal={proposal}
                hasTrigger={hasTrigger}
                onOpenThread={openThreadProp}
              />
            )}
          </div>

          {clarifyingPanel && (
            <div className='flex-shrink-0 mb-4'>{clarifyingPanel}</div>
          )}

          {lockReason !== null && <ActionsLockedNotice reason={lockReason} />}

          <div className='flex-1 flex flex-col'>
            <EntityComponent
              proposal={proposal}
              onUpdate={onUpdate}
              onActionUpdate={onActionUpdate}
              isProcessing={effectiveIsProcessing}
              onOpenThread={openThreadProp}
              optimisticMessage={optimisticMessage}
              {...entitySelectionProps}
            />
          </div>
        </div>
      </DeliveryStatesProvider>
    );
  }

  // Only offer the card-level Dismiss when there's a registered handler to
  // invoke (footer is in no-op mode) and the proposal isn't read-only —
  // fail-closed when no handler is registered (no provider, or footer hasn't
  // registered yet).
  const noOpDismissAction =
    !readOnly && noOpDismiss?.canDismiss
      ? {
          label: 'Dismiss',
          onClick: noOpDismiss.dismiss,
          color: 'secondary' as const,
          isDisabled: noOpDismiss.isDismissing,
        }
      : undefined;

  /* Non-table action UI — single scroll container (original behavior) */
  return (
    <DeliveryStatesProvider
      orgId={proposal.org}
      actionIds={deliveryActionIds}
      expectsDeliveryRows={expectsDeliveryRows}
    >
      <div className='flex-1 overflow-y-auto overflow-x-hidden relative z-[2]'>
        <div className='relative p-3 sm:p-4 lg:p-6 max-w-[1000px] mx-auto w-full'>
          {/* Action Overview when there's reasoning to show; otherwise the
              standalone trigger preview ("triggers as previous"). */}
          {hasReasoning ? (
            <ActionOverview
              proposal={visibleProposal}
              hasTrigger={hasTrigger}
              triggerActionIds={triggerActionIds}
              onOpenThread={openThreadProp}
            />
          ) : (
            <TriggerPreview
              proposal={visibleProposal}
              hasTrigger={hasTrigger}
              onOpenThread={openThreadProp}
              triggerActionIds={triggerActionIds}
            />
          )}

          {clarifyingPanel && <div className='mb-4'>{clarifyingPanel}</div>}

          {lockReason !== null && <ActionsLockedNotice reason={lockReason} />}

          {allActionsNoOp ? (
            <EmptyState
              icon={<CheckCircle className='size-6 text-fg-success-primary' />}
              title='Already up to date'
              description="Every proposed change already matches this record, so there's nothing to apply."
              size='md'
              action={noOpDismissAction}
            />
          ) : (
            <EntityComponent
              proposal={visibleProposal}
              onUpdate={onUpdate}
              onActionUpdate={onActionUpdate}
              isProcessing={effectiveIsProcessing}
              onOpenThread={openThreadProp}
              optimisticMessage={optimisticMessage}
              {...entitySelectionProps}
            />
          )}
        </div>
      </div>
    </DeliveryStatesProvider>
  );
}
